// src/api/authToken.js
//
// Firebase ID-token helpers shared by the axios instance. Kept free of any
// axios import so the auth-refresh logic can be unit-tested without pulling in
// axios's ESM build.
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../firebase/firebaseApp";

const auth = getAuth(firebaseApp);

/** The currently signed-in Firebase user, or null. */
export function getCurrentUser() {
  return auth.currentUser;
}

// Resolve a usable Firebase ID token for the signed-in user.
//
// `getIdToken()` returns the in-memory token while it still has more than ~5
// minutes of life and transparently performs a network refresh otherwise, so
// calling it on every request keeps the clinician signed in without any manual
// refresh — and without weakening security, since tokens stay short-lived
// (~1h) and the refresh is handled by the Firebase SDK using its securely
// stored refresh token. Pass `forceRefresh` to bypass the cache after the
// backend has rejected a token.
export async function resolveIdToken(forceRefresh = false) {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }
  return currentUser.getIdToken(forceRefresh);
}

// Builds an axios response error handler that recovers from an expired/invalid
// access token mid-session.
//
// A token can still be rejected after it was attached: it may have lapsed while
// the tab was asleep (so the SDK never proactively refreshed it), or there can
// be minor clock skew between the browser and the backend. On a 401 we force a
// single token refresh and replay the original request once.
//
// The `_pdxAuthRetried` guard keeps this to exactly one retry: if the freshly
// refreshed token is *still* rejected (a genuinely revoked/expired session),
// the error propagates so the app can route the user back to sign-in rather
// than looping silently.
export function createAuthRetryInterceptor(replay) {
  return async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      original &&
      !original._pdxAuthRetried &&
      getCurrentUser()
    ) {
      original._pdxAuthRetried = true;
      try {
        const token = await resolveIdToken(true);
        if (token) {
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${token}`;
          return replay(original);
        }
      } catch {
        // Refresh failed (e.g. the session was revoked). Fall through and let
        // the original 401 propagate to the caller.
      }
    }

    return Promise.reject(error);
  };
}

// Builds an axios response error handler that retries *idempotent* requests
// (GET/HEAD) through transient backend failures — 502/503/504, e.g. Cloud Run
// cold starts after the tab has been idle — with exponential backoff. This is
// deliberately status-based (not "any network error") so aborted/cancelled
// requests are never replayed. Non-idempotent methods and non-transient
// statuses propagate unchanged.
export function createTransientRetryInterceptor(
  replay,
  { retries = 2, baseDelayMs = 300 } = {},
) {
  const transientStatuses = new Set([502, 503, 504]);

  return async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const method = (original?.method || "get").toLowerCase();
    const isIdempotent = method === "get" || method === "head";

    if (original && isIdempotent && transientStatuses.has(status)) {
      const attempt = original._pdxTransientRetries || 0;

      if (attempt < retries) {
        original._pdxTransientRetries = attempt + 1;
        await new Promise((resolve) => {
          setTimeout(resolve, baseDelayMs * 2 ** attempt);
        });
        return replay(original);
      }
    }

    return Promise.reject(error);
  };
}
